/**
 * Sync Progress Component
 * Display sync progress and results
 */

import { SyncResult } from '../services/api';

interface SyncProgressProps {
  result: SyncResult | null;
  isLoading: boolean;
}

export default function SyncProgress({ result, isLoading }: SyncProgressProps) {
  if (isLoading) {
    return (
      <div className="p-4 border rounded-lg bg-brand-50 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-600 border-t-transparent" />
          <span className="text-brand-700">Syncing files...</span>
        </div>
      </div>
    );
  }

  if (!result) return null;

  const successRate =
    result.total > 0 ? Math.round((result.completed / result.total) * 100) : 0;
  const hasFailures = result.failed > 0;

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Summary Header */}
      <div className={`p-4 ${hasFailures ? 'bg-yellow-50' : 'bg-green-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hasFailures ? (
              <WarningIcon className="w-6 h-6 text-yellow-500" />
            ) : (
              <CheckCircleIcon className="w-6 h-6 text-green-500" />
            )}
            <span className="font-medium">
              Sync Complete: {result.completed}/{result.total} files (
              {successRate}%)
            </span>
          </div>
          <span className="text-sm text-gray-500">
            ID: {result.sync_id.slice(0, 8)}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              hasFailures ? 'bg-yellow-400' : 'bg-green-500'
            }`}
            style={{ width: `${successRate}%` }}
          />
        </div>
      </div>

      {/* Results Details */}
      {(result.results.completed.length > 0 ||
        result.results.failed.length > 0) && (
        <div className="divide-y">
          {result.results.failed.length > 0 && (
            <div className="p-4">
              <h4 className="text-sm font-medium text-red-600 mb-2">
                Failed ({result.failed})
              </h4>
              <ul className="space-y-2 text-sm">
                {result.results.failed.map((item, i) => (
                  <li
                    key={i}
                    className="flex justify-between items-start gap-4 text-red-700"
                  >
                    <span className="truncate flex-1">{item.file}</span>
                    <span className="text-xs text-red-500 whitespace-nowrap">
                      {item.error}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.results.completed.length > 0 && (
            <div className="p-4">
              <h4 className="text-sm font-medium text-green-600 mb-2">
                Completed ({result.completed})
              </h4>
              <ul className="space-y-1 text-sm max-h-48 overflow-y-auto">
                {result.results.completed.map((item, i) => (
                  <li key={i} className="text-gray-700 flex items-center gap-2">
                    <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="truncate">{item.file}</span>
                    {item.scenario_id && (
                      <span className="text-xs text-gray-400">
                        #{item.scenario_id}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
